import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientApi } from '../services/api';

const CLIENT_BASE = '/api/client/v1';
const dark = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px 14px', fontSize: 14, color: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
const darkArea = { ...dark, minHeight: 90, resize: 'vertical' };
const label = { fontSize: 11, fontWeight: 700, color: '#D72638', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, display: 'block' };

// ── Interest definitions ──────────────────────────────────────────────────
const INTERESTS = [
  { key: 'swimming',     label: 'Swimming',          icon: 'icon-swimming.png' },
  { key: 'running',      label: 'Running',            icon: 'icon-running.png' },
  { key: 'strength',     label: 'Strength Training',  icon: 'icon-strength.png' },
  { key: 'cycling',      label: 'Cycling',            icon: 'icon-cycling.png' },
  { key: 'basketball',   label: 'Basketball',         icon: 'icon-basketball.png' },
  { key: 'golf',         label: 'Golf',               icon: 'icon-golf.png' },
  { key: 'volleyball',   label: 'Volleyball',         icon: 'icon-volleyball.png' },
  { key: 'soccer',       label: 'Soccer',             icon: 'icon-ssoccer.png' },
  { key: 'hockey',       label: 'Hockey',             icon: 'icon-hockey.png' },
  { key: 'football',     label: 'Am. Football',       icon: 'icon-football.png' },
  { key: 'rugby',        label: 'Rugby',              icon: 'icon-rugby.png' },
  { key: 'baseball',     label: 'Baseball',           icon: 'icon-baseball.png' },
  { key: 'cricket',      label: 'Cricket',            icon: 'icon-cricket.png' },
  { key: 'crossfit',     label: 'CrossFit',           icon: 'icon-crossfit.png' },
  { key: 'nutrition',    label: 'Nutrition',          icon: 'icon-nutrition.png' },
  { key: 'supplements',  label: 'Supplements',        icon: 'icon-supplements.png' },
  { key: 'boxing',       label: 'Boxing',             icon: '/icons/icon-boxing.png' },
  { key: 'climbing',     label: 'Climbing',           icon: 'icon-climbing.png' },
  { key: 'skiing',       label: 'Skiing',             icon: 'icon-skiing.png' },
  { key: 'snowboarding', label: 'Snowboarding',       icon: 'icon-snowboarding.png' },
  { key: 'surfing',      label: 'Surfing',            icon: 'icon-surfing.png' },
  { key: 'hiking',       label: 'Hiking',             icon: 'icon-hiking.png' },
  { key: 'yoga',         label: 'Yoga',               icon: 'icon-yoga.png' },
];

function parseInterests(raw) {
  if (!raw) return [];
  try { const p = JSON.parse(raw); if (Array.isArray(p)) return p; } catch (_) {}
  return raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return new Date(dateStr).toLocaleDateString();
}

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ── DM Panel ──────────────────────────────────────────────────────────────
function DMPanel({ profile, myProfileId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    clientApi.get(`/social/dms/${profile.id}`)
      .then(d => setMessages(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [profile.id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  async function send(e) {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const msg = await clientApi.post(`/social/dms/${profile.id}`, { content: text.trim() });
      setMessages(prev => [...prev, msg]);
      setText('');
    } catch (err) { alert(err.message); }
    finally { setSending(false); }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 500 }} />
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 501, background: '#111', borderRadius: '18px 18px 0 0', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 10px', borderBottom: '1px solid #222' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Message {profile.display_name}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>
        <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading && <div style={{ textAlign: 'center', color: '#555', fontSize: 13 }}>Loading…</div>}
          {!loading && messages.length === 0 && <div style={{ textAlign: 'center', color: '#555', fontSize: 13 }}>No messages yet. Say hi!</div>}
          {messages.map(m => {
            const mine = m.sender_id === myProfileId;
            return (
              <div key={m.id} style={{ maxWidth: '78%', alignSelf: mine ? 'flex-end' : 'flex-start', background: mine ? '#D72638' : '#1A1A1A', borderRadius: 16, padding: '10px 14px' }}>
                <div style={{ fontSize: 14, color: '#fff', lineHeight: 1.45 }}>{m.content}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 3, textAlign: 'right' }}>{timeAgo(m.created_at)}</div>
              </div>
            );
          })}
        </div>
        <form onSubmit={send} style={{ padding: '12px 16px', borderTop: '1px solid #222', display: 'flex', gap: 10, alignItems: 'center', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Message…"
            style={{ flex: 1, background: '#1A1A1A', border: '1px solid #2E2E2E', borderRadius: 22, padding: '10px 16px', fontSize: 14, color: '#fff', outline: 'none' }} />
          <button type="submit" disabled={!text.trim() || sending}
            style={{ background: '#D72638', border: 'none', borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: !text.trim() || sending ? 0.5 : 1, flexShrink: 0, color: '#fff', fontSize: 16 }}>↑</button>
        </form>
      </div>
    </>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────
function EditModal({ profile, onClose, onSave }) {
  const [form, setForm] = useState({
    display_name:  profile.display_name  || '',
    bio:           profile.bio           || '',
    fitness_goals: profile.fitness_goals || '',
    interests:     parseInterests(profile.interests),
  });
  const [saving, setSaving] = useState(false);
  const [interestOpen, setInterestOpen] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function toggleInterest(key) {
    setForm(f => {
      const has = f.interests.includes(key);
      return { ...f, interests: has ? f.interests.filter(k => k !== key) : [...f.interests, key] };
    });
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, interests: JSON.stringify(form.interests) };
      const updated = await clientApi.put('/profile/me', payload);
      onSave({ ...updated, interests: JSON.stringify(form.interests) });
      onClose();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 500 }} />
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 501, background: '#111', borderRadius: '18px 18px 0 0', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 10px', borderBottom: '1px solid #222', flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Edit Profile</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={save} style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div><label style={label}>Display Name</label><input style={dark} value={form.display_name} onChange={e => set('display_name', e.target.value)} maxLength={80} /></div>
          <div><label style={label}>About Me</label><textarea style={darkArea} value={form.bio} onChange={e => set('bio', e.target.value)} maxLength={300} /></div>
          <div><label style={label}>Fitness Goals</label><textarea style={darkArea} value={form.fitness_goals} onChange={e => set('fitness_goals', e.target.value)} maxLength={300} /></div>

          {/* Interests picker */}
          <div>
            <label style={label}>Interests</label>

            {/* Selected chips */}
            {form.interests.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                {form.interests.map(key => {
                  const item = INTERESTS.find(i => i.key === key);
                  if (!item) return null;
                  return (
                    <div key={key} onClick={() => toggleInterest(key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(215,38,56,0.2)', border: '1px solid rgba(215,38,56,0.5)', borderRadius: 20, padding: '5px 10px', cursor: 'pointer' }}>
                      {item.icon && <img src={`/icons/${item.icon}`} alt="" style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }} />}
                      <span style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>{item.label}</span>
                      <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>×</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Dropdown toggle */}
            <button type="button" onClick={() => setInterestOpen(v => !v)}
              style={{ ...dark, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '10px 14px' }}>
              <span style={{ color: form.interests.length ? '#fff' : '#555' }}>
                {form.interests.length ? `${form.interests.length} selected` : 'Select interests…'}
              </span>
              <span style={{ color: '#555', fontSize: 12 }}>{interestOpen ? '▲' : '▼'}</span>
            </button>

            {/* Dropdown grid */}
            {interestOpen && (
              <div style={{ background: '#0D0D0D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, marginTop: 4, padding: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
                {INTERESTS.map(item => {
                  const selected = form.interests.includes(item.key);
                  return (
                    <div key={item.key} onClick={() => toggleInterest(item.key)}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '10px 6px', borderRadius: 12, cursor: 'pointer', background: selected ? 'rgba(215,38,56,0.2)' : 'rgba(255,255,255,0.04)', border: `1px solid ${selected ? 'rgba(215,38,56,0.5)' : 'transparent'}`, transition: 'all 0.15s' }}>
                      {item.icon
                        ? <img src={`/icons/${item.icon}`} alt="" style={{ width: 28, height: 28, filter: 'brightness(0) invert(1)', opacity: selected ? 1 : 0.55 }} />
                        : <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🥊</div>
                      }
                      <span style={{ fontSize: 10, color: selected ? '#fff' : '#888', fontWeight: selected ? 700 : 400, textAlign: 'center', lineHeight: 1.3 }}>{item.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button type="submit" disabled={saving}
            style={{ background: '#D72638', border: 'none', borderRadius: 14, padding: '14px 0', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1, fontFamily: 'inherit' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </form>
      </div>
    </>
  );
}

// ── Main profile page ─────────────────────────────────────────────────────
export default function Profile() {
  const { profileId } = useParams();
  const navigate = useNavigate();

  const [profile,   setProfile]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [following, setFollowing] = useState(false);
  const [frStatus,  setFrStatus]  = useState(null);
  const [showEdit,  setShowEdit]  = useState(false);
  const [showDM,    setShowDM]    = useState(false);
  const [myProfileId, setMyProfileId] = useState(null);
  const [activeTab,   setActiveTab]   = useState(null);
  const [tabData,     setTabData]     = useState({});
  const [tabLoading,  setTabLoading]  = useState(false);
  const [ready,       setReady]       = useState(!!localStorage.getItem('ptm_client_token'));

  // Trainers have ptm_token but no ptm_client_token — get a community token first
  useEffect(() => {
    if (ready) return;
    const trainerToken = localStorage.getItem('ptm_token');
    if (!trainerToken) { setReady(true); return; }
    fetch('/api/auth/community-token', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + trainerToken }
    })
      .then(r => r.json())
      .then(d => { if (d.token) localStorage.setItem('ptm_client_token', d.token); })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return;
    clientApi.get('/profile/me').then(me => {
      setMyProfileId(me.id);
      const id = profileId || me.id;
      const isOwn = !profileId || parseInt(profileId) === me.id;
      const endpoint = isOwn ? '/profile/me' : `/profile/${id}`;
      return clientApi.get(endpoint);
    }).then(data => {
      setProfile(data);
      setFollowing(data.is_following || false);
      setFrStatus(data.friend_status || null);
    }).catch(err => { alert(err.message); })
      .finally(() => setLoading(false));
  }, [profileId, ready]);

  const isOwn = profile && myProfileId && (profile.is_own || !profileId || parseInt(profileId) === myProfileId);

  async function openTab(tab) {
    if (activeTab === tab) { setActiveTab(null); return; }
    setActiveTab(tab);
    if (tabData[tab]) return;
    const id = isOwn ? myProfileId : (profileId || myProfileId);
    setTabLoading(true);
    try {
      const data = await clientApi.get(`/profile/${id}/${tab}`);
      setTabData(prev => ({ ...prev, [tab]: Array.isArray(data) ? data : [] }));
    } catch (_) {
      setTabData(prev => ({ ...prev, [tab]: [] }));
    } finally { setTabLoading(false); }
  }

  async function handleFollow() {
    try {
      const data = await clientApi.post(`/social/follow/${profile.id}`);
      setFollowing(data.following);
      setProfile(prev => ({
        ...prev,
        follower_count: data.following ? (prev.follower_count || 0) + 1 : Math.max(0, (prev.follower_count || 0) - 1),
      }));
    } catch (err) { alert(err.message); }
  }

  async function handleFriendRequest() {
    if (frStatus === 'pending' || frStatus === 'accepted') return;
    try {
      const data = await clientApi.post(`/social/friend-request/${profile.id}`);
      setFrStatus(data.status);
    } catch (err) { alert(err.message); }
  }

  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  async function handleAvatarUpload(e) {
    const file = e.target.files[0]; if (!file) return;
    const token = localStorage.getItem('ptm_client_token');
    const formData = new FormData(); formData.append('avatar', file);
    try {
      const res = await fetch(`${CLIENT_BASE}/profile/me/avatar`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProfile(prev => ({ ...prev, avatar_url: data.url }));
    } catch (err) { alert('Upload failed: ' + err.message); }
  }

  async function handleBannerUpload(e) {
    const file = e.target.files[0]; if (!file) return;
    const token = localStorage.getItem('ptm_client_token');
    const formData = new FormData(); formData.append('banner', file);
    try {
      const res = await fetch(`${CLIENT_BASE}/profile/me/banner`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProfile(prev => ({ ...prev, banner_url: data.url }));
    } catch (err) { alert('Upload failed: ' + err.message); }
  }

  if (loading) return (
    <div style={{ minHeight: '100dvh', background: '#080808', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #D72638', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!profile) return null;

  const selectedInterests = parseInterests(profile.interests);

  return (
    <div style={{ minHeight: '100dvh', background: '#080808', paddingBottom: 80, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', overflowX: 'hidden' }}>

      <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
      <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBannerUpload} />

      {profileId && (
        <button onClick={() => navigate(-1)} style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top) + 12px)', left: 14, zIndex: 100, background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 20 }}>‹</button>
      )}

      {/* ── Banner ────────────────────────────────────────────────── */}
      <div onClick={() => isOwn && bannerInputRef.current.click()}
        style={{ width: '100%', height: 180, position: 'relative', cursor: isOwn ? 'pointer' : 'default', background: '#111' }}>
        {profile.banner_url
          ? <img src={profile.banner_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isOwn && <span style={{ color: '#333', fontSize: 13 }}>Tap to add banner photo</span>}
            </div>
        }
        {isOwn && (
          <div style={{ position: 'absolute', bottom: 10, right: 12, background: 'rgba(0,0,0,0.6)', borderRadius: 20, padding: '6px 12px', fontSize: 12, color: '#fff' }}>Change banner</div>
        )}

        {/* Follow + Add Friend icon buttons on banner right — non-own profiles */}
        {!isOwn && profile && (
          <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 10, zIndex: 10 }}>
            <button onClick={e => { e.stopPropagation(); handleFollow(); }}
              style={{ width: 44, height: 44, borderRadius: '50%', background: following ? 'rgba(215,38,56,0.35)' : 'rgba(0,0,0,0.55)', border: `1.5px solid ${following ? 'rgba(215,38,56,0.7)' : 'rgba(255,255,255,0.25)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
              <img src="/icons/icon-follow.png" alt="Follow" style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }} />
            </button>
            <button onClick={e => { e.stopPropagation(); handleFriendRequest(); }}
              disabled={frStatus === 'pending' || frStatus === 'accepted'}
              style={{ width: 44, height: 44, borderRadius: '50%', background: frStatus ? 'rgba(215,38,56,0.35)' : 'rgba(0,0,0,0.55)', border: `1.5px solid ${frStatus ? 'rgba(215,38,56,0.7)' : 'rgba(255,255,255,0.25)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: frStatus ? 'default' : 'pointer', backdropFilter: 'blur(8px)', opacity: frStatus === 'pending' || frStatus === 'accepted' ? 0.6 : 1 }}>
              <img src="/icons/icon-add-friend.png" alt="Add Friend" style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }} />
            </button>
          </div>
        )}
      </div>

      {/* ── Avatar + action row ────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '0 16px 12px', marginTop: -44 }}>
        <div onClick={() => isOwn && avatarInputRef.current.click()}
          style={{ position: 'relative', width: 88, height: 88, borderRadius: '50%', border: '3px solid #080808', flexShrink: 0, cursor: isOwn ? 'pointer' : 'default', overflow: 'hidden', background: '#D72638' }}>
          {profile.avatar_url
            ? <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#fff' }}>{initials(profile.display_name)}</div>
          }
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 52 }}>
          {isOwn ? (
            <button onClick={() => setShowEdit(true)} style={{ border: '1.5px solid rgba(255,255,255,0.3)', background: 'none', borderRadius: 20, padding: '7px 16px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Edit Profile</button>
          ) : (
            <button onClick={() => setShowDM(true)} style={{ background: 'none', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 16 }}>✉</button>
          )}
        </div>
      </div>

      {/* ── Name ──────────────────────────────────────────────────── */}
      <div style={{ padding: '0 16px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile.display_name || 'Community Member'}</h2>
          {isOwn && (profile.first_name || profile.last_name) && (
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#666' }}>{[profile.first_name, profile.last_name].filter(Boolean).join(' ')}</p>
          )}
        </div>
        {!isOwn && profile && (
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <button onClick={handleFollow}
              style={{ width: 40, height: 40, borderRadius: '50%', background: following ? 'rgba(215,38,56,0.25)' : 'rgba(255,255,255,0.08)', border: `1.5px solid ${following ? 'rgba(215,38,56,0.7)' : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <img src="/icons/icon-follow.png" alt="Follow" style={{ width: 20, height: 20, filter: following ? 'none' : 'brightness(0) invert(1)', opacity: following ? 1 : 0.8 }} />
            </button>
            <button onClick={handleFriendRequest}
              disabled={frStatus === 'pending' || frStatus === 'accepted'}
              style={{ width: 40, height: 40, borderRadius: '50%', background: frStatus ? 'rgba(215,38,56,0.25)' : 'rgba(255,255,255,0.08)', border: `1.5px solid ${frStatus ? 'rgba(215,38,56,0.7)' : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: frStatus ? 'default' : 'pointer', opacity: frStatus === 'pending' || frStatus === 'accepted' ? 0.5 : 1 }}>
              <img src="/icons/icon-add-friend.png" alt="Add Friend" style={{ width: 20, height: 20, filter: frStatus ? 'none' : 'brightness(0) invert(1)', opacity: frStatus ? 1 : 0.8 }} />
            </button>
          </div>
        )}
      </div>

      {/* ── Stats ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', margin: '14px 16px', background: '#111', borderRadius: 14, padding: '16px 0' }}>
        {[['Posts', profile.post_count || 0, null], ['Followers', profile.follower_count || 0, 'followers'], ['Following', profile.following_count || 0, 'following']].map(([lbl, val, tab], i, arr) => (
          <div key={lbl} onClick={() => tab && openTab(tab)}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRight: i < arr.length - 1 ? '1px solid #222' : 'none', cursor: tab ? 'pointer' : 'default' }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{val}</span>
            <span style={{ fontSize: 11, color: activeTab === tab && tab ? '#D72638' : '#666', marginTop: 2 }}>{lbl}</span>
          </div>
        ))}
      </div>

      {/* ── Social tabs ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', margin: '0 16px 4px', borderBottom: '1px solid #1E1E1E' }}>
        {[['friends','Friends'],['followers','Followers'],['following','Following']].map(([key, lbl]) => (
          <button key={key} onClick={() => openTab(key)}
            style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', padding: '12px 0', position: 'relative', fontFamily: 'inherit' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: activeTab === key ? '#fff' : '#555' }}>{lbl}</span>
            {activeTab === key && <div style={{ position: 'absolute', bottom: 0, left: '15%', right: '15%', height: 2, background: '#D72638', borderRadius: 2 }} />}
          </button>
        ))}
      </div>

      {/* ── Tab content ───────────────────────────────────────────── */}
      {activeTab && (
        <div style={{ margin: '0 16px 8px' }}>
          {tabLoading ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#D72638' }}>
              <div style={{ width: 28, height: 28, border: '3px solid #D72638', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : (tabData[activeTab] || []).length === 0 ? (
            <p style={{ textAlign: 'center', color: '#444', fontSize: 13, padding: '24px 0', fontStyle: 'italic' }}>
              {activeTab === 'friends' ? 'No friends yet' : activeTab === 'followers' ? 'No followers yet' : 'Not following anyone yet'}
            </p>
          ) : (tabData[activeTab] || []).map(user => {
            const ini = (user.display_name || '?').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();
            return (
              <div key={user.id} onClick={() => navigate(`/profile/${user.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #111', cursor: 'pointer' }}>
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#D72638', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                  {user.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: 42, height: 42, objectFit: 'cover' }} /> : <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{ini}</span>}
                </div>
                <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#fff' }}>{user.display_name}</span>
                <span style={{ fontSize: 20, color: '#444' }}>›</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Bio sections ──────────────────────────────────────────── */}
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(profile.bio || isOwn) && (
          <div style={{ background: '#111', borderRadius: 14, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#D72638', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>About Me</div>
            <p style={{ margin: 0, fontSize: 14, color: profile.bio ? '#DDD' : '#444', lineHeight: 1.6, fontStyle: profile.bio ? 'normal' : 'italic' }}>
              {profile.bio || (isOwn ? 'Tap Edit Profile to add your bio…' : '')}
            </p>
          </div>
        )}
        {(profile.fitness_goals || isOwn) && (
          <div style={{ background: '#111', borderRadius: 14, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#D72638', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Fitness Goals</div>
            <p style={{ margin: 0, fontSize: 14, color: profile.fitness_goals ? '#DDD' : '#444', lineHeight: 1.6, fontStyle: profile.fitness_goals ? 'normal' : 'italic' }}>
              {profile.fitness_goals || (isOwn ? 'What are you training for?' : '')}
            </p>
          </div>
        )}

        {/* ── Interests with icons ──────────────────────────── */}
        {(selectedInterests.length > 0 || isOwn) && (
          <div style={{ background: '#111', borderRadius: 14, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#D72638', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Interests</div>
            {selectedInterests.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {selectedInterests.map(key => {
                  const item = INTERESTS.find(i => i.key === key);
                  if (!item) return null;
                  return (
                    <div key={key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, width: 62, overflow: "hidden" }}>
                      <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'rgba(215,38,56,0.15)', border: '1.5px solid rgba(215,38,56,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {item.icon
                          ? <img src={`/icons/${item.icon}`} alt="" style={{ width: 26, height: 26, filter: 'brightness(0) invert(1)' }} />
                          : <span style={{ fontSize: 18 }}>🥊</span>
                        }
                      </div>
                      <span style={{ fontSize: 10, color: "#AAA", textAlign: "center", lineHeight: 1.3, wordBreak: "break-word" }}>{item.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 14, color: '#444', fontStyle: 'italic' }}>Tap Edit Profile to add your interests…</p>
            )}
          </div>
        )}
      </div>

      {/* ── Bottom nav ────────────────────────────────────────────── */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: 64, background: 'rgba(8,8,8,0.95)', backdropFilter: 'blur(12px)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-around', zIndex: 100, paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {[
          { key: 'feed',    icon: '/icons/icon-social-home.png',              label: 'Feed',    path: '/feed' },
          { key: 'search',  icon: '/icons/icon-social-search.png',     label: 'Search',  path: '/feed' },
          { key: 'workouts',icon: '/icons/icon-social-schedule.png',   label: 'Workouts',path: '/feed' },
          { key: 'profile', icon: '/icons/icon-user-profile.png',      label: 'Profile', path: '/profile' },
        ].map(item => (
          <button key={item.key} onClick={() => navigate(item.path)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 12px', opacity: item.key === 'profile' ? 1 : 0.4 }}>
            <img src={item.icon} alt={item.label} style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />
            <span style={{ fontSize: 10, color: item.key === 'profile' ? '#D72638' : '#fff', fontWeight: item.key === 'profile' ? 700 : 400 }}>{item.label}</span>
          </button>
        ))}
      </div>

      {showEdit && <EditModal profile={profile} onClose={() => setShowEdit(false)} onSave={updated => setProfile(prev => ({ ...prev, ...updated }))} />}
      {showDM && <DMPanel profile={profile} myProfileId={myProfileId} onClose={() => setShowDM(false)} />}
    </div>
  );
}
